import { forwardRef, type ReactNode, type Ref, type CSSProperties } from 'react';
import { useStableId } from '@repletoryreach/sdk-utils/hooks';
import type { BaseProps } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps } from '../../core/_internal/factory.js';
import './InputBase.css';

export interface InputWrapperBaseProps extends Omit<BaseProps, 'size'> {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  withAsterisk?: boolean;
  /** Optional left icon/text. */
  leftSection?: ReactNode;
  /** Optional right icon/text. */
  rightSection?: ReactNode;
  /** Pixel width allocated to each section. */
  leftSectionWidth?: number;
  rightSectionWidth?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

export interface InputWrapperRenderProps {
  inputId: string;
  describedBy?: string;
  invalid: boolean;
  className: string;
  style: CSSProperties;
}

export interface InputWrapperProps extends InputWrapperBaseProps {
  inputId?: string;
  className?: string;
  style?: CSSProperties;
  /** The actual input (or input-like) element. */
  children: (renderProps: InputWrapperRenderProps) => ReactNode;
}

/** Shared label/description/error scaffold around every input control. */
export const InputWrapper = forwardRef(function InputWrapper(
  props: InputWrapperProps,
  ref: Ref<HTMLDivElement>
): ReactNode {
  const {
    label,
    description,
    error,
    required,
    withAsterisk = required,
    leftSection,
    rightSection,
    leftSectionWidth = 36,
    rightSectionWidth = 36,
    inputId,
    size = 'sm',
    className,
    style,
    children
  } = props;

  const auto = useStableId('rr-input');
  const id = inputId ?? auto;
  const descId = description ? `${id}-desc` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [descId, errId].filter(Boolean).join(' ') || undefined;
  const invalid = !!error;

  const base = useBaseAttrs(
    'rr-input-wrapper',
    { ...props, invalid, required, size },
    [`rr-input-wrapper--size-${size}`]
  );
  // `props` here carries the wrapper's OWN props (label, sections, and a
  // `children` render function), so they are named as consumed rather than
  // spread onto the <div>.
  const { dom } = splitBaseProps({ ...props, invalid, required, size }, [
    'label', 'description', 'error', 'withAsterisk',
    'leftSection', 'rightSection', 'leftSectionWidth', 'rightSectionWidth',
    'inputId'
  ]);

  return (
    <div {...dom} ref={ref} className={[base.className, className].filter(Boolean).join(' ')} style={{ ...base.style, ...style }} {...base.attrs}>
      {label ? (
        <label htmlFor={id} className="rr-input-wrapper__label">
          {label}
          {withAsterisk ? <span className="rr-input-wrapper__required" aria-hidden> *</span> : null}
        </label>
      ) : null}
      <div
        className="rr-input-wrapper__control"
        style={{
          ['--rr-input-left' as string]: leftSection ? `${leftSectionWidth}px` : undefined,
          ['--rr-input-right' as string]: rightSection ? `${rightSectionWidth}px` : undefined
        } as CSSProperties}
      >
        {leftSection ? <div className="rr-input-wrapper__section rr-input-wrapper__section--left">{leftSection}</div> : null}
        {children({ inputId: id, describedBy, invalid, className: 'rr-input-control', style: {} })}
        {rightSection ? <div className="rr-input-wrapper__section rr-input-wrapper__section--right">{rightSection}</div> : null}
      </div>
      {/*
        The description sits BELOW the control, not between the label and it.
        A hint like "At least 8 characters, with an uppercase and a lowercase
        letter, a number and a symbol." is guidance for filling the field in,
        so it belongs where the user looks after reading the label: at the
        field itself. `aria-describedby` still points here, so the association
        is unchanged for assistive technology.
      */}
      {description ? (
        <div id={descId} className="rr-input-wrapper__description">{description}</div>
      ) : null}
      {error ? <div id={errId} className="rr-input-wrapper__error">{error}</div> : null}
    </div>
  );
});
